/**
 * QuoteBillableExpenses — as despesas COBRÁVEIS de um orçamento, na tela do
 * próprio orçamento.
 *
 * Este é o ponto de entrada da feature, e ele fica aqui de propósito: o
 * ORÇAMENTO é o dono da despesa cobrável (ver `src/lib/billableExpense.ts`).
 * Criar em `/financeiro` e ver um documento mudar sozinho seria ação à
 * distância — por isso o formulário do financeiro só habilita a marcação quando
 * foi aberto a partir daqui (prop `ownerQuoteId`).
 */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Button3D } from '@/components/ui/button-3d';
import { Plus, Receipt, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { useRole } from '@/hooks/useRole';
import { useDeleteFinanceEntry, useFinanceEntriesRaw } from '@/hooks/useFinance';
import { BILLABLE_LOCKED_MESSAGE, isQuoteLockedForBillable } from '@/lib/billableExpense';
import { formatBRL } from '@/lib/financeLabels';
import type { FinanceEntryWithStatus } from '@/types/database';

interface Props {
  quoteId: string;
  quoteStatus: string | null | undefined;
}

export function QuoteBillableExpenses({ quoteId, quoteStatus }: Props) {
  // Gate de PAPEL: `finance_entries` é owner/admin-only na RLS, então para um
  // `member` a lista viria vazia e o botão criaria um lançamento que ele não
  // consegue ler. Some inteiro em vez de mentir.
  const { canSeeFinance } = useRole();
  const { entries } = useFinanceEntriesRaw();
  const deleteEntry = useDeleteFinanceEntry();
  const navigate = useNavigate();

  /**
   * O `ownerQuoteId` viaja no state da navegação: é ele que HABILITA marcar a
   * despesa como cobrável, e o orçamento é o dono dela. Sem ele, o formulário
   * abre com a marcação desabilitada (que é o comportamento correto para quem
   * entra por /financeiro).
   */
  const abrirNova = () =>
    navigate('/financeiro/novo', {
      state: { prefill: newExpensePrefill, defaultKind: 'expense', ownerQuoteId: quoteId },
    });
  const abrirEdicao = (expense: FinanceEntryWithStatus) =>
    navigate(`/financeiro/${expense.id}/editar`, {
      state: { defaultKind: 'expense', ownerQuoteId: quoteId },
    });

  const billableExpenses = useMemo(
    () => entries.filter((e) => e.billable && e.quote_id === quoteId),
    [entries, quoteId],
  );

  /**
   * MEMOIZADO de propósito. O `prefill` entra nas deps do efeito que reseta o
   * formulário do modal; um literal novo a cada render faz esse efeito rodar de
   * novo e APAGAR o que o usuário acabou de digitar — sem erro nenhum, só um
   * "Informe uma descrição" inexplicável ao salvar. E este componente re-renderiza
   * sempre que a lista de lançamentos muda.
   */
  const newExpensePrefill = useMemo(
    () => ({ kind: 'expense' as const, quote_id: quoteId, billable: true }),
    [quoteId],
  );

  // Orçamento já aprovado/concluído: mexer no total é grave. A seção continua
  // VISÍVEL (as despesas já lançadas fazem parte do documento) mas em leitura.
  const locked = isQuoteLockedForBillable(quoteStatus);
  const isOfflineQuote = String(quoteId).startsWith('offline_');

  if (!canSeeFinance) return null;
  // Orçamento ainda não sincronizado não tem id real para ancorar o vínculo.
  if (isOfflineQuote && billableExpenses.length === 0) return null;

  const openNew = () => {
    if (locked) {
      toast.warning(BILLABLE_LOCKED_MESSAGE);
      return;
    }
    abrirNova();
  };

  const handleDelete = (entry: FinanceEntryWithStatus) => {
    if (locked) {
      toast.warning(BILLABLE_LOCKED_MESSAGE);
      return;
    }
    deleteEntry.mutate({ id: entry.id });
  };

  return (
    <>
      <Card>
        <CardContent className="space-y-3 p-4">
          <div className="flex items-center gap-2">
            <Receipt className="h-4 w-4 text-muted-foreground" />
            <h3 className="flex-1 text-sm font-semibold">Despesas cobráveis</h3>
            {!locked && !isOfflineQuote && (
              <Button3D size="sm" onClick={openNew}>
                <Plus className="mr-1.5 h-4 w-4" />
                Adicionar
              </Button3D>
            )}
          </div>

          {billableExpenses.length === 0 ? (
            <p className="text-xs text-muted-foreground">
              Gastos que você repassa ao cliente (pedágio, material, frete de terceiro).
              Cada um vira uma linha deste orçamento e entra no total.
            </p>
          ) : (
            <ul className="space-y-2">
              {billableExpenses.map((expense) => (
                <li key={expense.id} className="flex items-center gap-2 rounded-lg border p-2">
                  <button
                    type="button"
                    className="min-w-0 flex-1 text-left"
                    onClick={() => {
                      if (locked) {
                        toast.warning(BILLABLE_LOCKED_MESSAGE);
                        return;
                      }
                      abrirEdicao(expense);
                    }}
                  >
                    <p className="truncate text-sm font-medium">{expense.description}</p>
                    <p className="text-xs text-muted-foreground">
                      {formatBRL(Number(expense.amount ?? 0))}
                    </p>
                  </button>
                  {!locked && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 shrink-0 text-destructive hover:bg-destructive/10 hover:text-destructive"
                      onClick={() => handleDelete(expense)}
                      disabled={deleteEntry.isPending}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}

          {locked && billableExpenses.length > 0 && (
            <p className="text-xs text-muted-foreground">{BILLABLE_LOCKED_MESSAGE}</p>
          )}
        </CardContent>
      </Card>

    </>
  );
}
